// Los formatos personalizados de la marca activa, cargados UNA vez y compartidos —
// ADR-2026-10-20-formatos-personalizados.
//
// El selector de formato se monta en muchos lugares a la vez (cada card del chat, Crear,
// los modos): con `useBrandProfile` en cada uno serían varias consultas por instancia. Acá
// se resuelve la marca activa una vez, se leen sus formatos (`descarga_config`, el mismo
// cache de las descargas) y se llena el registro de `formatoPersonalizado`, que leen las
// funciones que arman el pedido. Se recarga al cambiar de marca.

import { useEffect, useSyncExternalStore } from 'react';
import { supabase } from '../lib/supabase';
import {
  fetchConfigDeDescarga,
  guardarConfigDeDescarga,
  invalidarCacheDeDescarga,
  leerConfigDeDescargaParaEscribir,
  type PerfilDeDescarga,
} from './configDeDescarga';
import { registrarFormatosPersonalizados } from './formatoPersonalizado';
import { resolveMarcaActivaId } from './marcaActiva';

interface Estado {
  brandId: string | null;
  formatos: PerfilDeDescarga[];
  cargado: boolean;
}

const VACIO: Estado = { brandId: null, formatos: [], cargado: false };
let estado: Estado = VACIO;
let cargando: Promise<void> | null = null;
/** Sube con cada cambio de marca: una carga que arrancó antes no publica lo de la marca vieja
 *  encima de la nueva (ADR-2026-10-21-revision-greptile-196-formatos-personalizados). */
let vuelta = 0;
const oyentes = new Set<() => void>();

function publicar(siguiente: Estado) {
  estado = siguiente;
  registrarFormatosPersonalizados(siguiente.formatos);
  for (const o of oyentes) o();
}

async function cargar(): Promise<void> {
  const mia = vuelta;
  const publicarSiSigue = (s: Estado) => {
    if (mia === vuelta) publicar(s);
  };
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return publicarSiSigue({ brandId: null, formatos: [], cargado: true });
    const { data: perfil } = await supabase.from('profiles').select('id_company').eq('id', user.id).maybeSingle();
    const companyId = (perfil as { id_company?: string | null } | null)?.id_company ?? null;
    const brandId = companyId ? await resolveMarcaActivaId(companyId) : null;
    const config = brandId ? await fetchConfigDeDescarga(brandId) : null;
    publicarSiSigue({ brandId, formatos: config?.perfiles ?? [], cargado: true });
  } catch {
    publicarSiSigue({ brandId: null, formatos: [], cargado: true });
  }
}

/** Arranca la carga si todavía no se hizo. Idempotente. */
export function asegurarFormatosPersonalizados(): Promise<void> {
  if (!cargando) cargando = cargar();
  return cargando;
}

if (typeof window !== 'undefined') {
  // Funciones avanzadas (o este mismo store) guardó la lista de la marca.
  window.addEventListener('atlas:descarga-config-guardada', (e) => {
    const d = (e as CustomEvent<{ brandId: string; config: { perfiles: PerfilDeDescarga[] } }>).detail;
    if (d?.brandId && d.brandId === estado.brandId) publicar({ ...estado, formatos: d.config.perfiles });
  });
  window.addEventListener('atlas:brand-changed', () => {
    invalidarCacheDeDescarga();
    vuelta++;
    cargando = null;
    // Hasta que llegue la nueva, no hay marca: nadie guarda un formato en la que se fue.
    publicar(VACIO);
    if (oyentes.size > 0) void asegurarFormatosPersonalizados();
  });
}

/**
 * Suma un formato a la marca activa y lo deja listo para elegir. Se guarda en la misma
 * lista que "Formato de descarga" de Funciones avanzadas: es el mismo formato. Si la marca
 * no tenía ninguno, la config nace APAGADA (`activo: false`): eso hace que la hoja de
 * descarga siga sin preguntar en las OTRAS imágenes; las que se generen con este formato
 * se convierten igual, porque llevan la copia en su fila.
 */
export async function agregarFormatoPersonalizado(
  formato: PerfilDeDescarga,
): Promise<{ ok: boolean; error?: string }> {
  await asegurarFormatosPersonalizados();
  const brandId = estado.brandId;
  if (!brandId) return { ok: false, error: 'Todavía no hay una marca para guardar el formato.' };
  // Se relee de la base: otra pestaña (o Funciones avanzadas) pudo cambiar la lista. Si no se
  // pudo leer, no se guarda: escribir sobre un "no hay" falso borraba los formatos que había.
  invalidarCacheDeDescarga();
  const leida = await leerConfigDeDescargaParaEscribir(brandId);
  if (!leida.ok) return { ok: false, error: 'No pudimos leer los formatos de la marca. Probá de nuevo.' };
  const actual = leida.config;
  const siguiente = actual
    ? { ...actual, perfiles: [...actual.perfiles, formato] }
    : { version: 2 as const, activo: false, siempre: null, perfiles: [formato] };
  const r = await guardarConfigDeDescarga(brandId, siguiente);
  if (r.ok && estado.brandId === brandId) publicar({ brandId, formatos: siguiente.perfiles, cargado: true });
  return r;
}

const suscribir = (o: () => void) => {
  oyentes.add(o);
  return () => void oyentes.delete(o);
};

/** Los formatos personalizados de la marca activa, para los selectores. */
export function useFormatosPersonalizados(): Estado {
  useEffect(() => {
    void asegurarFormatosPersonalizados();
  }, []);
  return useSyncExternalStore(suscribir, () => estado, () => estado);
}
